import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { toggleColorMode } from '@diwata/ironvale'
import { filterCommands, pageForPath, type Command } from '@/lib/commandPalette'
import './command-palette.css'

// The command catalogue. Nav mirrors the Layout sidebar; actions are the couple
// of global verbs a keyboard-first user reaches for. Pure data — `run` is
// resolved below so the list stays testable via lib/commandPalette.
const COMMANDS: Command[] = [
  // ── Navigate ──────────────────────────────────────────────────────────────
  { id: 'nav-home', title: 'Home', group: 'Navigate', page: 'home', to: '/home', keywords: ['dashboard', 'start'] },
  { id: 'nav-briefing', title: 'Briefing', group: 'Navigate', page: 'briefing', to: '/briefing', keywords: ['digest', 'today'] },
  { id: 'nav-constellation', title: 'Constellation', group: 'Navigate', page: 'constellation', to: '/graph', keywords: ['stars', 'graph', 'sky'] },
  { id: 'nav-domains', title: 'Domains', group: 'Navigate', page: 'domains', to: '/domains', keywords: ['regions'] },
  { id: 'nav-springs', title: 'Springs', group: 'Navigate', page: 'springs', to: '/pipelines', keywords: ['sources', 'ingest', 'pipelines'] },
  { id: 'nav-signals', title: 'Signals', group: 'Navigate', page: 'signals', to: '/signals', keywords: ['alerts', 'events'] },
  { id: 'nav-ledger', title: 'Ledger', group: 'Navigate', page: 'ledger', to: '/ledger', keywords: ['rows', 'lake', 'records'] },
  { id: 'nav-missions', title: 'Missions', group: 'Navigate', page: 'missions', to: '/missions', keywords: ['queue', 'tasks', 'work'] },
  { id: 'nav-agents', title: 'Agents', group: 'Navigate', page: 'agents', to: '/agents', keywords: ['familiars', 'automation'] },
  { id: 'nav-trove', title: 'Trove', group: 'Navigate', page: 'trove', to: '/trove', keywords: ['vault', 'artifacts', 'bronze'] },
  { id: 'nav-commune', title: 'Commune', group: 'Navigate', page: 'commune', to: '/commune', keywords: ['community', 'people'] },
  { id: 'nav-settings', title: 'Settings', group: 'Navigate', page: 'settings', to: '/settings', keywords: ['preferences', 'account'] },
  { id: 'nav-plan', title: 'Plan', group: 'Navigate', to: '/pricing', keywords: ['pricing', 'billing', 'tier'] },
  { id: 'nav-map', title: 'How it connects', group: 'Navigate', to: '/map', keywords: ['map', 'help', 'overview'] },

  // ── Actions ───────────────────────────────────────────────────────────────
  // Home-page verbs: the quick actions a keyboard-first user reaches for from
  // /home. Scoped page:'home' so they hoist to the top of an empty query there.
  { id: 'act-ask-tala', title: 'Ask Tala', group: 'Actions', page: 'home', to: '/familiar', hint: '✦ your familiar', keywords: ['familiar', 'chat', 'question', 'assistant'] },
  { id: 'act-judge-signals', title: 'Judge signals', group: 'Actions', page: 'home', to: '/needs', hint: 'needs you', keywords: ['triage', 'inbox', 'decide', 'review', 'proposals'] },
  { id: 'act-run-mission', title: 'Run a mission', group: 'Actions', page: 'home', to: '/missions', hint: 'on Missions', keywords: ['start', 'launch', 'work', 'new mission'] },
  // Same create-open mechanic as the Home quick action: plain /domains — the
  // Domains page owns creation. If Home grows a ?create=1 deep-link, mirror it
  // here so both paths land in the same open-creator state.
  { id: 'act-new-domain', title: 'New domain', group: 'Actions', page: 'domains', to: '/domains', hint: 'on Domains', keywords: ['create', 'add', 'region'] },
  { id: 'act-appearance', title: 'Appearance', group: 'Actions', action: 'appearance', hint: 'fonts · theme · density', keywords: ['theme', 'fonts', 'customize', 'look'] },
  { id: 'act-theme', title: 'Toggle light / dark', group: 'Actions', action: 'toggle-theme', keywords: ['dark mode', 'light mode', 'theme', 'contrast'] },
]

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listId = useId()

  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const currentPage = pageForPath(pathname)
  const results = useMemo(
    () => filterCommands(COMMANDS, query, currentPage),
    [query, currentPage],
  )

  // Fresh open: clear the query, reset the cursor, focus the field.
  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(0)
    // focus after the portal has mounted
    const id = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open])

  // Keep the cursor in range and pinned to the top as the result set changes.
  useEffect(() => {
    setActive(0)
  }, [query])

  // Keep the active row visible.
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open, results.length])

  if (!open) return null

  function runCommand(cmd: Command) {
    onClose()
    if (cmd.action === 'toggle-theme') {
      toggleColorMode()
      return
    }
    // 'appearance' and every nav command resolve to a route.
    const to = cmd.to ?? (cmd.action === 'appearance' ? '/settings' : undefined)
    if (to) navigate(to)
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (results.length) setActive((i) => (i + 1) % results.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (results.length) setActive((i) => (i - 1 + results.length) % results.length)
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        if (results.length) setActive(results.length - 1)
        break
      case 'Enter': {
        e.preventDefault()
        const cmd = results[active]
        if (cmd) runCommand(cmd)
        break
      }
      case 'Escape':
        e.preventDefault()
        onClose()
        break
      case 'Tab':
        // Focus-trapped: the input is the only focusable stop, so hold it here.
        e.preventDefault()
        break
    }
  }

  const activeId = results[active] ? `${listId}-opt-${results[active].id}` : undefined

  return createPortal(
    <div
      className="dw-cmdk-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="dw-cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="dw-cmdk-search">
          <span className="dw-cmdk-search-glyph" aria-hidden="true">
            ⌕
          </span>
          <input
            ref={inputRef}
            className="dw-cmdk-input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            aria-label="Search commands"
            placeholder="Search commands, pages, actions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          <span className="dw-cmdk-hintkey" aria-hidden="true">
            esc
          </span>
        </div>

        {results.length === 0 ? (
          <div className="dw-cmdk-empty">No commands match “{query.trim()}”.</div>
        ) : (
          <ul className="dw-cmdk-list" role="listbox" id={listId} aria-label="Commands" ref={listRef}>
            {results.map((cmd, i) => {
              const isActive = i === active
              return (
                <li
                  key={cmd.id}
                  id={`${listId}-opt-${cmd.id}`}
                  role="option"
                  aria-selected={isActive}
                  data-active={isActive}
                  className={`dw-cmdk-item${isActive ? ' dw-cmdk-item--active' : ''}`}
                  onMouseMove={() => setActive(i)}
                  onClick={() => runCommand(cmd)}
                >
                  <span className="dw-cmdk-item-glyph" aria-hidden="true">
                    {cmd.group === 'Actions' ? '✦' : '›'}
                  </span>
                  <span className="dw-cmdk-item-title">{cmd.title}</span>
                  {cmd.hint && <span className="dw-cmdk-item-hint">{cmd.hint}</span>}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  )
}
